import { mdiAlertCircle, mdiInformation } from '@mdi/js';
import Icon from '@mdi/react';
import React from 'react';
import * as S from './notice.styles';
import { NoticeTone } from './notice.styles';

/**
 * A standing explanation on a screen: why this list looks the way it does, and what it means for the work.
 *
 * Not a toast and not an error dialog. A toast reports something that just happened and then leaves; this stays as
 * long as the state it describes, which is what a list filtered to halted payers needs (FE-93, asked for by Jordan
 * Kelley). Added to `base/` with Vadim's go-ahead rather than hand-rolled in the screen that needed it first (NS-1) —
 * the next screen with a standing caveat gets the same thing instead of a second one.
 *
 * `role="status"` and not `alert`: the text explains the state a person navigated to on purpose. An assertive
 * announcement interrupts a screen reader mid-sentence, which is for something that went wrong.
 *
 * **There is deliberately no way to make it `alert`**, and the failure case is the reason rather than the exception.
 * On this platform a failed request already interrupts: the console's own error dialog opens, modal, and aria-hides
 * the page behind it. A notice explaining what the page now shows is what someone finds AFTER dismissing that dialog
 * — by which time the element has sat in a hidden subtree for seconds, so `alert` would announce nothing; and where
 * it did announce, it would be the second interruption for one event. Use `attention` for a state to act on and
 * `warning` for one that stops work; neither needs to shout (the denials page's failed-report notice, 2026-09-22).
 */
export interface INoticeProps {
    /** Which ramp to use. Warning is the default: these usually explain why something is not going out. */
    tone?: NoticeTone;
    /** An mdi path, when the default for the tone is not the right picture. */
    icon?: string;
    children: React.ReactNode;
    className?: string;
}

const DEFAULT_ICON: Record<NoticeTone, string> = {
    warning: mdiAlertCircle,
    attention: mdiAlertCircle,
    info: mdiInformation,
};

export function Notice({ tone = 'warning', icon, children, className }: INoticeProps): React.ReactElement {
    return (
        <S.Wrapper tone={tone} role="status" className={className}>
            <S.IconSlot tone={tone}>
                <Icon path={icon || DEFAULT_ICON[tone]} size={0.7} />
            </S.IconSlot>
            <S.Body>{children}</S.Body>
        </S.Wrapper>
    );
}
